import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, CheckCheck, RefreshCw, ExternalLink, AlertTriangle, Info, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

const LEVEL_META = {
  info:  { color: "#2FB999", Icon: Info },
  warn:  { color: "#D8B23E", Icon: AlertTriangle },
  error: { color: "#FF5A5F", Icon: XCircle },
};

/**
 * Admin dashboard notifications tab.
 *
 * Feeds off `/api/admin/notifications` — currently populated by the daily
 * TMT price-sync job.  Kept generic so future automations (payout failures,
 * KYC reminders, etc.) can slot in without UI changes.
 */
export function AdminNotifications({ api, onUnreadChange }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);   // /price-sync/status
  const [running, setRunning] = useState(false);
  const [tataStatus, setTataStatus] = useState(null);
  const [tataRunning, setTataRunning] = useState(false);
  const [tataPaste, setTataPaste] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, s, ts] = await Promise.all([
        api.get("/admin/notifications?limit=50"),
        api.get("/admin/price-sync/status").catch(() => ({ data: null })),
        api.get("/admin/price-sync/tata/status").catch(() => ({ data: null })),
      ]);
      setItems(n.data.items || []);
      setUnread(n.data.unread || 0);
      onUnreadChange && onUnreadChange(n.data.unread || 0);
      setStatus(s.data);
      setTataStatus(ts.data);
    } catch (e) {
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, [api, onUnreadChange]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await api.post(`/admin/notifications/${id}/read`);
      await load();
    } catch { toast.error("Could not mark as read"); }
  };

  const markAll = async () => {
    try {
      const r = await api.post("/admin/notifications/mark-all-read");
      toast.success(`Marked ${r.data.marked} as read`);
      await load();
    } catch { toast.error("Could not mark all"); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/price-sync/run");
      if (r.data.ok) {
        toast.success(`Prices updated · ₹${Number(r.data.new_price).toLocaleString("en-IN")}/MT from ${r.data.row_count} data points`);
      } else {
        toast.error("Scraper failed — see the new error notification below");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Manual scrape failed");
    } finally {
      setRunning(false);
    }
  };

  const runTataNow = async () => {
    setTataRunning(true);
    try {
      const r = await api.post("/admin/price-sync/tata/run");
      if (r.data.ok) toast.success(`Tata scan OK · ${r.data.brand_count} brands ready for approval`);
      else toast.error("Tata scan failed — see notifications & your email");
      await load();
    } catch (e) { toast.error("Tata scan failed"); }
    finally { setTataRunning(false); }
  };

  const submitTataPaste = async () => {
    if (!tataPaste.trim() || tataPaste.length < 200) { toast.error("Paste the full HTML from the Tata nexARC price page"); return; }
    setTataRunning(true);
    try {
      const r = await api.post("/admin/price-sync/tata/paste", { html: tataPaste });
      if (r.data.ok) { toast.success(`Parsed ${r.data.brand_count} brands — review below`); setTataPaste(""); }
      else toast.error(r.data.detail || "Could not parse pasted HTML");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Paste failed"); }
    finally { setTataRunning(false); }
  };

  const approveTata = async (batchId) => {
    if (!window.confirm("Publish these rates site-wide? They'll go live instantly on the Price Pulse terminal and Daily Rates.")) return;
    try {
      const r = await api.post(`/admin/price-sync/tata/${batchId}/approve`);
      toast.success(`${r.data.brand_count} brand rates went live`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };

  const discardTata = async (batchId) => {
    if (!window.confirm("Discard this pending batch?")) return;
    try {
      await api.post(`/admin/price-sync/tata/${batchId}/discard`);
      toast.success("Batch discarded");
      await load();
    } catch { toast.error("Discard failed"); }
  };

  const nextRun = status?.next_run_ist ? new Date(status.next_run_ist) : null;
  const lastRun = status?.last_run;
  const tataNext = tataStatus?.next_run_ist ? new Date(tataStatus.next_run_ist) : null;
  const tataBatch = tataStatus?.latest_batch;

  return (
    <div data-testid="admin-notifications" className="space-y-8">
      {/* Sync control panel */}
      <div className="border border-white/10 rounded-sm bg-[#0a0912] p-6 md:p-7 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#B87333] to-transparent opacity-50" />
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-end">
          <div>
            <span className="overline text-[#B87333]">/ Automated Job</span>
            <h2 className="font-serif text-3xl mt-2">Daily TMT Price Sync</h2>
            <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
              A scheduler scrapes <a data-testid="price-sync-source" href={status?.target_url || "https://www.steelmenu.in/tmt_prices_in_india.html"} target="_blank" rel="noreferrer noopener" className="text-[#B87333] hover:underline inline-flex items-center gap-1">steelmenu.in <ExternalLink size={11} /></a> every day at <b className="text-[#F5F5F0]">09:00 IST</b>, aggregates the median TMT-bar price and writes it into the Daily Price Manager. If the scrape fails you'll see an error card below.
            </p>
            <ul className="mt-4 grid sm:grid-cols-3 gap-4 font-body text-xs">
              <li>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">Next run</div>
                <div data-testid="price-sync-next-run" className="text-[#F5F5F0] flex items-center gap-1.5">
                  <Clock size={12} className="text-[#B87333]" />
                  {nextRun ? nextRun.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}
                  <span className="text-[#8A8A82]"> IST</span>
                </div>
              </li>
              <li>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">Last run</div>
                <div data-testid="price-sync-last-run" className="text-[#F5F5F0]">
                  {lastRun?.started_at ? new Date(lastRun.started_at).toLocaleString("en-IN") : "Never"}
                  {" · "}
                  <span className={lastRun?.ok ? "text-[#2FB999]" : "text-[#FF5A5F]"}>{lastRun ? (lastRun.ok ? "OK" : "FAIL") : "—"}</span>
                </div>
              </li>
              <li>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">Last median price</div>
                <div data-testid="price-sync-last-price" className="text-[#F5F5F0]">
                  {lastRun?.aggregated_price ? `₹${Number(lastRun.aggregated_price).toLocaleString("en-IN")}/MT` : "—"}
                  {lastRun?.row_count ? <span className="text-[#8A8A82]"> · {lastRun.row_count} rows</span> : null}
                </div>
              </li>
            </ul>
          </div>
          <div className="flex gap-2 shrink-0">
            <button data-testid="price-sync-refresh" type="button" onClick={load}
                    className="border border-white/20 hover:border-[#B87333] text-[#C9C9C2] font-grotesk text-[10px] uppercase tracking-[0.22em] px-4 py-2.5 rounded-sm inline-flex items-center gap-2">
              <RefreshCw size={12} /> Refresh
            </button>
            <button data-testid="price-sync-run-now" type="button" onClick={runNow} disabled={running}
                    className="bg-[#B87333] hover:bg-[#A05F26] text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-2.5 rounded-sm inline-flex items-center gap-2 disabled:opacity-50">
              <RefreshCw size={12} className={running ? "animate-spin" : ""} /> {running ? "Scraping…" : "Run now"}
            </button>
          </div>
        </div>
      </div>

      {/* Tata nexARC · Approve & Go Live */}
      <div className="border border-white/10 rounded-sm bg-[#0a0912] p-6 md:p-7 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#A78BFA] to-transparent opacity-50" />
        <div className="grid md:grid-cols-[1fr_auto] gap-4 items-end">
          <div>
            <span className="overline text-[#A78BFA]">/ Brand-Level Rates</span>
            <h2 className="font-serif text-3xl mt-2">Tata nexARC Scanner · 09:30 IST</h2>
            <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
              Every morning at 9:30 IST we scan <a href={tataStatus?.target_url || "https://www.tatanexarc.com/steel/steel-prices-in-india/"} target="_blank" rel="noreferrer noopener" className="text-[#A78BFA] hover:underline inline-flex items-center gap-1">tatanexarc.com <ExternalLink size={11}/></a> for Tata Tiscon, JSW Neosteel and other TMT brand rates. Nothing goes live until you click <b>Approve & Go Live</b>. If the scan fails you'll get an email + red notification below.
            </p>
            <ul className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 font-body text-xs">
              <li><span className="text-[#8A8A82]">Next run:</span> <span className="text-[#F5F5F0]">{tataNext ? tataNext.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) + " IST" : "—"}</span></li>
              <li><span className="text-[#8A8A82]">Pending batches:</span> <span className="text-[#A78BFA] font-bold" data-testid="tata-pending-count">{tataStatus?.pending_count || 0}</span></li>
            </ul>
          </div>
          <button data-testid="tata-run-now" type="button" onClick={runTataNow} disabled={tataRunning}
                  className="border border-[#A78BFA]/60 hover:bg-[#A78BFA] hover:text-black text-[#A78BFA] font-grotesk text-[10px] uppercase tracking-[0.22em] px-4 py-2.5 rounded-sm inline-flex items-center gap-2 disabled:opacity-50">
            <RefreshCw size={12} className={tataRunning ? "animate-spin" : ""} /> {tataRunning ? "Scanning…" : "Run now"}
          </button>
        </div>

        {/* Escape hatch — paste HTML manually if WAF blocks */}
        <details className="mt-4">
          <summary className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#A78BFA] cursor-pointer">
            WAF blocking the scan? Paste Tata HTML manually
          </summary>
          <div className="mt-3">
            <textarea
              data-testid="tata-paste-html"
              value={tataPaste}
              onChange={(e) => setTataPaste(e.target.value)}
              placeholder="Open tatanexarc.com/steel/steel-prices-in-india, right-click the price table → Inspect → Copy outerHTML → paste here."
              className="w-full min-h-[80px] bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-mono text-[11px] text-[#C9C9C2] focus:outline-none focus:border-[#A78BFA]"
            />
            <button data-testid="tata-paste-submit" type="button" onClick={submitTataPaste} disabled={tataRunning}
                    className="mt-2 bg-[#A78BFA] hover:bg-[#8b6fe4] text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2 rounded-sm disabled:opacity-50">
              Parse pasted HTML
            </button>
          </div>
        </details>

        {/* Pending batch card */}
        {tataBatch && tataBatch.status === "pending" && (
          <div data-testid="tata-pending-batch" className="mt-5 border border-[#A78BFA]/40 bg-[#A78BFA]/[0.05] rounded-sm p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#A78BFA]">Pending · awaiting approval</div>
                <div className="font-body text-xs text-[#8A8A82] mt-1">Scraped {new Date(tataBatch.created_at).toLocaleString("en-IN")} · {tataBatch.row_count} rows · {(tataBatch.brand_rates || []).length} brands</div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`tata-discard-${tataBatch.id}`} type="button" onClick={() => discardTata(tataBatch.id)}
                        className="border border-white/20 hover:border-[#FF5A5F] hover:text-[#FF5A5F] font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-sm">
                  Discard
                </button>
                <button data-testid={`tata-approve-${tataBatch.id}`} type="button" onClick={() => approveTata(tataBatch.id)}
                        className="bg-[#2FB999] hover:bg-[#28a086] text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                  <CheckCheck size={12}/> Approve & Go Live
                </button>
              </div>
            </div>
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {(tataBatch.brand_rates || []).map((br) => (
                <li key={br.brand} data-testid={`tata-brand-${br.brand.replace(/\s+/g,'-')}`} className="border border-white/10 rounded-sm px-3 py-2.5 bg-black/30">
                  <div className="font-serif text-sm text-[#F5F5F0] truncate">{br.brand}</div>
                  <div className="mt-0.5 flex items-baseline gap-2">
                    <span className="font-serif text-xl text-[#A78BFA] tabular-nums">₹{Number(br.price).toLocaleString("en-IN")}</span>
                    <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82]">/MT · {br.row_count} rows</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Notification stack */}
      <div className="border border-white/10 rounded-sm bg-[#0a0a0a] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Bell size={14} className="text-[#B87333]" />
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">Notifications</span>
            {unread > 0 && (
              <span data-testid="notification-unread-count"
                    className="min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#B87333] text-black text-[10px] font-bold flex items-center justify-center">
                {unread}
              </span>
            )}
          </div>
          <button data-testid="mark-all-read" type="button" onClick={markAll} disabled={unread === 0}
                  className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9C9C2] hover:text-[#B87333] disabled:opacity-40 inline-flex items-center gap-1.5">
            <CheckCheck size={12} /> Mark all read
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center font-body text-sm text-[#8A8A82]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Bell size={22} className="text-[#8A8A82] mx-auto mb-3" />
            <p className="font-body text-sm text-[#C9C9C2]">No notifications yet.</p>
            <p className="font-body text-xs text-[#8A8A82] mt-1">Automated jobs will drop status cards here.</p>
          </div>
        ) : (
          <ul>
            <AnimatePresence initial={false}>
              {items.map((n) => {
                const meta = LEVEL_META[n.level] || LEVEL_META.info;
                return (
                  <motion.li
                    key={n.id}
                    layout
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    data-testid={`notification-row-${n.id}`}
                    className={`px-5 py-4 border-b border-white/[0.04] last:border-b-0 flex items-start gap-4 ${n.read ? "opacity-70" : ""}`}
                  >
                    <span className="grid place-items-center w-9 h-9 rounded-sm shrink-0"
                          style={{ color: meta.color, border: `1px solid ${meta.color}55`, background: `${meta.color}10` }}>
                      <meta.Icon size={15} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-serif text-lg text-[#F5F5F0]">{n.title}</span>
                        {!n.read && (
                          <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#B87333] px-1.5 py-0.5 rounded-sm border border-[#B87333]/50 bg-[#B87333]/[0.06]">
                            New
                          </span>
                        )}
                      </div>
                      <p className="font-body text-sm text-[#C9C9C2] mt-1 leading-relaxed whitespace-pre-line">{n.message}</p>
                      <div className="mt-2 flex items-center gap-3 flex-wrap font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82]">
                        <span>{n.kind}</span>
                        <span>·</span>
                        <span>{new Date(n.created_at).toLocaleString("en-IN")}</span>
                        {n.meta?.new_price && (
                          <>
                            <span>·</span>
                            <span className="text-[#B87333]">₹{Number(n.meta.new_price).toLocaleString("en-IN")}/MT</span>
                          </>
                        )}
                        {n.meta?.delta_pct != null && n.meta.delta_pct !== 0 && (
                          <span className={n.meta.delta_pct > 0 ? "text-[#2FB999]" : "text-[#FF5A5F]"}>
                            {n.meta.delta_pct > 0 ? "▲" : "▼"} {Math.abs(n.meta.delta_pct)}%
                          </span>
                        )}
                      </div>
                    </div>
                    {!n.read && (
                      <button
                        type="button"
                        onClick={() => markRead(n.id)}
                        data-testid={`notification-mark-read-${n.id}`}
                        className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#B87333] shrink-0"
                      >
                        Mark read
                      </button>
                    )}
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </ul>
        )}
      </div>
    </div>
  );
}
